import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Body1, Body2, Header2, Paper } from "@activecollab/components";
import PropTypes from "prop-types";
import NotFoundSlate from "~/components/EmptySlate/NotFoundSlate.js";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { Page } from "~/components/Page/index.jsx";
import { Resolver } from "~/components/Resolver.jsx";
import { TrashSlate } from "~/components/TrashSlate/TrashSlate.js";
import { fetchObjectLastVisitTimestamp } from "~/store/actions/actions.lastVisit.jsx";
import { fetchProject } from "~/store/actions/actions.projects.jsx";
import { loadTask, restoredFromTrash } from "~/store/actions/actions.tasks.jsx";
import { permanentlyDelete } from "~/store/actions/actions.trash.jsx";

import { getTaskListForTaskId } from "~/store/selectors/selector.taskLists";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
const NotFound = ({ className }) => {
  const { t } = useTranslation();

  return (
    <Paper className={className}>
      <NotFoundSlate
        header={t("This task is not found")}
        pageName={t("projects page")}
        pageLink="/projects"
      />
    </Paper>
  );
};

NotFound.propTypes = {
  className: PropTypes.string,
};

export const TaskTrashSlate = ({ projectId, taskId, children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const task = useSelector((state) => getTaskById(state, taskId));
  const taskIsTrashed = task ? task.is_trashed : false;
  const [notFoundShow, setNotFoundShow] = useState(() => {
    if (task && typeof task.project_id !== "undefined") {
      return task.project_id !== projectId; //case when you go back in browser and task is moved to another project
    }
    return false;
  });
  const loggedUser = useSelector(getLoggedUser);
  const isOwner = loggedUser.class === "Owner";
  const taskList = useSelector((state) => getTaskListForTaskId(state, projectId, taskId));

  const canUserRestoreTask = (isOwner || loggedUser.id === task?.trashed_by_id) && !taskList?.is_trashed;

  const type = task?.class || "task";

  const { t } = useTranslation();

  const getData = useCallback(async () => {
    if (!taskIsTrashed) {
      await dispatch(fetchObjectLastVisitTimestamp("Task", taskId));
    }

    await dispatch(fetchProject(projectId)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });

    await dispatch(loadTask(projectId, taskId)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });
  }, [dispatch, projectId, taskId, taskIsTrashed]);

  const handleRestore = useCallback(async () => {
    await dispatch(restoredFromTrash(task?.id));
    await dispatch(loadTask(task?.project_id, task?.id));
  }, [dispatch, task?.id, task?.project_id]);

  const handleDelete = useCallback(async () => {
    await dispatch(permanentlyDelete("Task", task?.id));
    navigate(`/projects/${task?.project_id}`);
  }, [dispatch, navigate, task?.id, task?.project_id]);

  useGlobalPrivateChannelListener("DataObjectDeleted", (data) => {
    if (data.type === "Task" && data.id === taskId) {
      setNotFoundShow(true);
    }
  });

  if (notFoundShow) {
    return <NotFound className="tw-flex tw-flex-col tw-h-full tw-mb-8" />;
  }

  return (
    <Resolver
      key={`task-${taskId}-${projectId}`}
      loadingComponent={<Page.Loader />}
      errorComponent={
        <Page
          title={t("Not Found")}
          className="tw-h-full"
        >
          <NotFound
            className="tw-grow"
            type={type.toLowerCase()}
          />
        </Page>
      }
      callback={getData}
    >
      <TrashSlate
        type={type}
        name="projectName"
        className="tw-h-full tw-items-center"
        subtitle={
          <>
            <Body1 align="center">{t("This task is deleted")}.</Body1>
            {canUserRestoreTask ? (
              <Body2
                align="center"
                color="secondary"
                className="tw-mt-2"
              >
                {t("If you want to work on it again, recover it")}.
              </Body2>
            ) : null}
          </>
        }
        title={
          <>
            <Header2 className="tw-mr-1 tw-inline">{t("Task")}:</Header2>
            {task?.name}
          </>
        }
        isTrashed={task?.is_trashed}
        trashedOn={task?.trashed_on}
        trashedById={task?.trashed_by_id}
        createdById={task?.created_by_id}
        createdByName={task?.created_by_name}
        createdOn={task?.created_on}
        canRestore={canUserRestoreTask}
        canPermanentlyDelete={isOwner}
        onRestore={handleRestore}
        onPermanentlyDelete={handleDelete}
      >
        {children}
      </TrashSlate>
    </Resolver>
  );
};

TaskTrashSlate.propTypes = {
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  children: PropTypes.node,
};
